'use client';
import { useVoiceEditors } from '@/components/voice/context';

import { Box, Flex, Stack } from '@chakra-ui/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { WorkspaceCanvasPanel } from '@/components/canvas/WorkspaceCanvasPanel';
import { WorkspaceDesignSystemPanel } from '@/components/design-system/WorkspaceDesignSystemPanel';
import { DownloadsProvider } from '@/components/downloads/DownloadsProvider';
import type { WebsitePreviewMetadata } from '@/components/github/GitHubLivePreview';
import { WorkspaceWebsitePreviewPanel } from '@/components/github/WorkspaceWebsitePreviewPanel';
import { WorkspaceLivingDocsPanel } from '@/components/reports/WorkspaceLivingDocsPanel';
import {
  LivingDocsPanelProvider,
  useLivingDocsPanel,
  useLivingDocsPanelActions,
} from '@/contexts/LivingDocsPanelContext';
import { useProject } from '@/contexts/ProjectContext';
import { useDesktopViewport } from '@/hooks/useDesktopViewport';
import { isCanvasEnabledClient } from '@/lib/canvas/feature-flags';
import {
  CANVAS_AVAILABILITY_CHANGED_EVENT,
  type CanvasAvailabilityChange,
  type CanvasArtifactDetail,
  type CanvasToolMetadata,
} from '@/lib/canvas/shared';
import { VoiceBoundary } from '@/components/voice/VoiceBoundary';
import {
  isGitHubWebsiteEnabledClient,
  isGitHubBindingEnabledClient,
  isGitHubLivePreviewEnabledClient,
} from '@/lib/github/feature-flags';
import { requestWorkspaceWebsitePreview } from '@/lib/github/preview/workspace-launch';
import { isProjectDesignSystemEnabledClient } from '@/lib/project-design-system/feature-flags';
import { AppRail } from './AppRail';
import { getPrimarySection } from './primary-navigation';
import { PrimaryRail } from './PrimaryRail';
import { AppRailProvider, useAppRail } from './AppRailContext';
import { AppShellToolbarFrame } from './AppShellToolbar';
import { MobileThreadsProvider } from './MobileThreadsContext';
import { AppSidebar } from './Sidebar';
import { SidePanelTools } from './SidePanelTools';
import { SidePanelSizeProvider } from './SidePanelSize';
import { AppShellHeaderProvider } from './AppShellHeader';

interface AppLayoutProps {
  children: ReactNode;
  initialIsAdmin?: boolean;
  authPreview?: boolean;
}

function GlobalLivingDocsPanel({
  authPreview,
  onboardingLocked,
}: {
  authPreview: boolean;
  onboardingLocked: boolean;
}) {
  const { user } = useAuth();
  const availabilityAuthUserRef = useRef(user);
  availabilityAuthUserRef.current = user;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { currentProject, projects, switchProject, loading: projectLoading } = useProject();
  const isFullPageDoc = /^\/app\/docs\/[^/]+$/.test(pathname);
  const {
    activeReportId,
    activeWebsitePreview,
    activeCanvas,
    open,
    view,
    closePanel,
    openPanel,
    openWebsitePreview,
    openCanvas,
    openDesignSystem,
    updateCanvas,
    clearCanvas,
    setCanvasSelection,
    registerCanvasSelectionClear,
    selectReport,
    setView,
  } = useLivingDocsPanel();
  const { scopeWebsitePreviewToSession } = useLivingDocsPanelActions();
  const isDesktop = useDesktopViewport();
  const githubPreviewEnabled = isGitHubWebsiteEnabledClient() && isGitHubLivePreviewEnabledClient();
  const canvasClientEnabled = isCanvasEnabledClient();
  const designSystemEnabled = isProjectDesignSystemEnabledClient();
  const [githubAvailability, setGitHubAvailability] = useState<{
    projectId: string;
    userId: string;
    enabled: boolean;
  } | null>(null);
  const githubPreviewAvailable =
    githubAvailability?.projectId === currentProject?.id &&
    githubAvailability?.userId === user?.uid &&
    githubAvailability?.enabled === true;
  const [canvasAvailability, setCanvasAvailability] = useState<{
    projectId: string | null;
    userId: string | null;
    enabled: boolean;
  } | null>(null);
  const canvasAvailable =
    canvasAvailability &&
    canvasAvailability.projectId === (currentProject?.id || null) &&
    canvasAvailability.userId === (user?.uid || null)
      ? canvasAvailability.enabled
      : null;
  const { setEditorAvailability } = useAppRail();
  useEffect(() => {
    setEditorAvailability({
      userId: user?.uid || '', projectId: currentProject?.id || '',
      site: githubPreviewEnabled && githubPreviewAvailable && !onboardingLocked,
      canvas: canvasClientEnabled && canvasAvailable === true && isDesktop === true && !onboardingLocked,
    });
  }, [user?.uid, currentProject?.id, githubPreviewEnabled, githubPreviewAvailable, canvasClientEnabled, canvasAvailable, isDesktop, onboardingLocked, setEditorAvailability]);
  useVoiceEditors(
    canvasClientEnabled && canvasAvailable === true && isDesktop === true && !onboardingLocked,
    designSystemEnabled && isDesktop === true && !onboardingLocked,
    githubPreviewEnabled && githubPreviewAvailable && !onboardingLocked
  );
  const previewLaunchVersionRef = useRef(0);
  const canvasAvailabilityGenerationRef = useRef(0);
  const linkedCanvasLoadVersionRef = useRef(0);
  const linkedPreviewLoadVersionRef = useRef(0);
  const restoredEditorRef = useRef<string | null>(null);
  const currentProjectIdRef = useRef(currentProject?.id || null);
  const currentUserIdRef = useRef(user?.uid || null);
  const activeCanvasIdRef = useRef(activeCanvas?.id || null);
  currentProjectIdRef.current = currentProject?.id || null;
  currentUserIdRef.current = user?.uid || null;
  activeCanvasIdRef.current = activeCanvas?.id || null;
  const linkedCanvasId = searchParams.get('canvas');
  const linkedPreviewId = searchParams.get('preview');
  const linkedEditor = searchParams.get('editor');
  const linkedSessionId = searchParams.get('session') || searchParams.get('sessionId');
  const linkedMode = searchParams.get('mode');
  const searchParamsString = searchParams.toString();

  useEffect(() => {
    if (!linkedPreviewId || !user || authPreview || !githubPreviewEnabled || projectLoading) return;
    const version = ++linkedPreviewLoadVersionRef.current;
    const controller = new AbortController();
    let switching = false;
    void (async() => {
      const token = await user.getIdToken();
      const response = await fetch(`/api/github-previews/${encodeURIComponent(linkedPreviewId)}/session?status=1`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      const payload = await response.json() as { projectId?: string; preview?: WebsitePreviewMetadata; error?: string };
      if (controller.signal.aborted || linkedPreviewLoadVersionRef.current !== version) return;
      if (!response.ok || !payload.preview || !payload.projectId) throw new Error('This website preview is unavailable to your account.');
      if (payload.projectId !== currentProject?.id) {
        if (!projects.some(project => project.id === payload.projectId)) throw new Error('You no longer have access to this preview’s project.');
        switching = true;
        switchProject(payload.projectId, { preserveRoute: true });
        return;
      }
      if (linkedSessionId) scopeWebsitePreviewToSession(linkedSessionId);
      openWebsitePreview(payload.preview);
    })().catch(error => {
      if (!controller.signal.aborted) toast.error(error instanceof Error ? error.message : 'Unable to open website preview.');
    }).finally(() => {
      if (!switching && !controller.signal.aborted && linkedPreviewLoadVersionRef.current === version) {
        const query = new URLSearchParams(searchParamsString);
        query.delete('preview');
        router.replace(`${pathname}?${query}`, { scroll: false });
      }
    });
    return () => controller.abort();
  }, [linkedPreviewId, linkedSessionId, scopeWebsitePreviewToSession, user, authPreview, githubPreviewEnabled, projectLoading, currentProject?.id, projects, switchProject, openWebsitePreview, searchParamsString, router, pathname]);
  useEffect(() => {
    if (
      pathname !== '/app/workspace' ||
      !currentProject?.id ||
      !user ||
      authPreview ||
      onboardingLocked ||
      (linkedEditor !== 'website' && isDesktop !== true) ||
      linkedCanvasId ||
      linkedPreviewId ||
      (linkedEditor !== 'canvas' && linkedEditor !== 'design-system' && linkedEditor !== 'website') ||
      (linkedEditor === 'website' && (!githubPreviewEnabled || !githubPreviewAvailable)) ||
      (linkedEditor === 'canvas' && (!canvasClientEnabled || canvasAvailable !== true)) ||
      (linkedEditor === 'design-system' && !designSystemEnabled)
    ) {
      restoredEditorRef.current = null;
      return;
    }
    const key = JSON.stringify([user.uid, currentProject.id, linkedSessionId, linkedEditor]);
    if (restoredEditorRef.current === key) return;
    restoredEditorRef.current = key;
    if (linkedEditor === 'canvas') {
      if (!linkedSessionId) clearCanvas();
      openCanvas();
    }
    else if (linkedEditor === 'website') openWebsitePreview();
    else openDesignSystem();
  }, [
    authPreview,
    canvasAvailable,
    canvasClientEnabled,
    currentProject?.id,
    designSystemEnabled,
    isDesktop,
    linkedCanvasId,
    linkedPreviewId,
    githubPreviewEnabled,
    githubPreviewAvailable,
    linkedEditor,
    linkedSessionId,
    onboardingLocked,
    openCanvas,
    clearCanvas,
    openWebsitePreview,
    openDesignSystem,
    pathname,
    user,
  ]);

  useEffect(() => {
    if (!open || view !== 'website-preview') previewLaunchVersionRef.current += 1;
  }, [open, view]);

  useEffect(() => {
    const receiveAvailability = (event: Event) => {
      const detail = (event as CustomEvent<CanvasAvailabilityChange>).detail;
      if (
        !detail ||
        typeof detail.enabled !== 'boolean' ||
        detail.projectId !== currentProjectIdRef.current ||
        detail.userId !== currentUserIdRef.current
      )
        return;
      // A completed library request is authoritative; don't let an older probe overwrite it.
      canvasAvailabilityGenerationRef.current += 1;
      setCanvasAvailability(detail);
    };
    window.addEventListener(CANVAS_AVAILABILITY_CHANGED_EVENT, receiveAvailability);
    return () => window.removeEventListener(CANVAS_AVAILABILITY_CHANGED_EVENT, receiveAvailability);
  }, []);

  useEffect(() => {
    const projectId = currentProject?.id;
    const availabilityScope = {
      projectId: projectId || null,
      userId: availabilityAuthUserRef.current?.uid || null,
    };
    const generation = ++canvasAvailabilityGenerationRef.current;
    setCanvasAvailability(null);
    if (!projectId || !availabilityAuthUserRef.current || !canvasClientEnabled) {
      setCanvasAvailability({ ...availabilityScope, enabled: false });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    void availabilityAuthUserRef.current
      .getIdToken()
      .then(token =>
        fetch(`/api/canvas?projectId=${encodeURIComponent(projectId)}&availability=true`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }),
      )
      .then(async response => {
        const payload = (await response.json().catch(() => ({}))) as { enabled?: boolean };
        if (!cancelled && generation === canvasAvailabilityGenerationRef.current) {
          setCanvasAvailability({
            ...availabilityScope,
            enabled: response.ok && payload.enabled === true,
          });
        }
      })
      .catch(() => {
        if (!cancelled && generation === canvasAvailabilityGenerationRef.current) {
          setCanvasAvailability({ ...availabilityScope, enabled: false });
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [canvasClientEnabled, currentProject?.id, user?.uid]);

  useEffect(() => {
    const projectId = currentProject?.id;
    if (!projectId || !user || !githubPreviewEnabled) {
      setGitHubAvailability(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const loadStatus = async() => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/integrations/github/status?projectId=${encodeURIComponent(projectId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (cancelled) return;
        setGitHubAvailability({
          projectId,
          userId: user.uid,
          enabled:
            response.ok &&
            payload?.installation?.status === 'connected' &&
            payload?.binding?.status === 'active' &&
            isGitHubBindingEnabledClient(payload.binding),
        });

      } catch {
        if (!cancelled) setGitHubAvailability({ projectId, userId: user.uid, enabled: false });
      }
    };

    void loadStatus();
    window.addEventListener('focus', loadStatus);
    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener('focus', loadStatus);
    };
  }, [currentProject?.id, githubPreviewEnabled, user]);

  const launchWebsitePreview = useCallback(async() => {
    const projectId = currentProject?.id;
    const actorUserId = user?.uid;
    if (!projectId || !actorUserId) return false;
    const launchVersion = ++previewLaunchVersionRef.current;
    const result = await requestWorkspaceWebsitePreview(projectId, actorUserId);
    if (
      !result ||
      previewLaunchVersionRef.current !== launchVersion ||
      currentProjectIdRef.current !== projectId ||
      currentUserIdRef.current !== actorUserId
    ) {
      return false;
    }
    const query = new URLSearchParams();
    query.set('session', result.sourceSessionId);
    query.set('editor', 'website');
    // Claim the destination before navigation can mount its chat or clean up the old one.
    scopeWebsitePreviewToSession(result.sourceSessionId);
    router.push(`/app/workspace?${query.toString()}`, { scroll: false });
    openWebsitePreview(result.preview);
    return true;
  }, [currentProject?.id, openWebsitePreview, router, scopeWebsitePreviewToSession, user?.uid]);

  const handleWebsitePreviewPanelOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        openPanel();
        return;
      }
      previewLaunchVersionRef.current += 1;
      closePanel();
      setView('document');
    },
    [closePanel, openPanel, setView],
  );

  const ensureWorkspaceChat = useCallback(() => {
    if (pathname === '/app/workspace' && (!linkedMode || linkedMode === 'chat')) return;
    const query = new URLSearchParams();
    if (linkedSessionId) query.set('session', linkedSessionId);
    router.push(`/app/workspace${query.size ? `?${query.toString()}` : ''}`, { scroll: false });
  }, [linkedMode, linkedSessionId, pathname, router]);

  const handleWebsitePreviewOpen = useCallback(() => {
    ensureWorkspaceChat();
    openWebsitePreview();
  }, [ensureWorkspaceChat, openWebsitePreview]);

  const handleCanvasOpen = useCallback(
    (canvas?: CanvasToolMetadata) => {
      ensureWorkspaceChat();
      openCanvas(canvas);
    },
    [ensureWorkspaceChat, openCanvas]
  );
  const handleDesignSystemOpen = useCallback(() => {
    ensureWorkspaceChat();
    openDesignSystem();
  }, [ensureWorkspaceChat, openDesignSystem]);
  const handleDesignSystemPanelOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) openDesignSystem();
      else {
        closePanel();
        setView('document');
        const query = new URLSearchParams(searchParamsString);
        if (query.get('editor') === 'design-system') query.delete('editor');
        const next = query.toString();
        router.replace(`${pathname}${next ? `?${next}` : ''}`, { scroll: false });
      }
    },
    [closePanel, openDesignSystem, pathname, router, searchParamsString, setView],
  );
  const handleCanvasPanelOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) openCanvas();
      else {
        closePanel();
        setView('document');
        const query = new URLSearchParams(searchParamsString);
        if (query.get('editor') !== 'canvas') return;
        query.delete('editor');
        const next = query.toString();
        router.replace(`${pathname}${next ? `?${next}` : ''}`, { scroll: false });
      }
    },
    [closePanel, openCanvas, pathname, router, searchParamsString, setView],
  );

  const handleCanvasSelect = useCallback(
    (canvas: CanvasToolMetadata) => {
      if (activeCanvas?.id === canvas.id) updateCanvas(canvas);
      else openCanvas(canvas);
    },
    [activeCanvas?.id, openCanvas, updateCanvas],
  );

  useEffect(() => {
    const projectId = currentProject?.id;
    if (!canvasClientEnabled || !linkedCanvasId || !user || !projectId) return;
    const loadVersion = ++linkedCanvasLoadVersionRef.current;
    const initialActiveCanvasId = activeCanvasIdRef.current;
    let waitingForProjectSwitch = false;

    const consumeDeepLink = () => {
      const nextSearchParams = new URLSearchParams(searchParamsString);
      nextSearchParams.delete('canvas');
      const nextQuery = nextSearchParams.toString();
      router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ''}`, { scroll: false });
    };
    if (initialActiveCanvasId === linkedCanvasId) {
      consumeDeepLink();
      return;
    }

    void user
      .getIdToken()
      .then(async token => {
        const response = await fetch(`/api/canvas/${encodeURIComponent(linkedCanvasId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = (await response.json().catch(() => ({}))) as {
          canvas?: CanvasArtifactDetail;
        };
        if (
          linkedCanvasLoadVersionRef.current === loadVersion &&
          activeCanvasIdRef.current === initialActiveCanvasId &&
          response.ok &&
          payload.canvas
        ) {
          if (payload.canvas.projectId !== projectId) {
            if (projects.some(project => project.id === payload.canvas!.projectId)) {
              waitingForProjectSwitch = true;
              switchProject(payload.canvas.projectId, { preserveRoute: true });
            }
            return;
          }
          openCanvas({
            id: payload.canvas.id,
            title: payload.canvas.title,
            status: payload.canvas.status,
            version: payload.canvas.currentVersion,
            lastSuccessfulVersion: payload.canvas.lastSuccessfulVersion,
          });
        }
      })
      .catch(() => {
        // The consumed deep link remains closed when it is missing or no longer authorized.
      })
      .finally(() => {
        if (!waitingForProjectSwitch && linkedCanvasLoadVersionRef.current === loadVersion) {
          consumeDeepLink();
        }
      });
    return () => {
      if (linkedCanvasLoadVersionRef.current === loadVersion) {
        linkedCanvasLoadVersionRef.current += 1;
      }
    };
  }, [
    canvasClientEnabled,
    currentProject?.id,
    linkedCanvasId,
    openCanvas,
    pathname,
    projects,
    router,
    searchParamsString,
    switchProject,
    user,
  ]);

  useEffect(() => {
    if (canvasAvailable === false && activeCanvas?.id) clearCanvas(activeCanvas.id);
    if (isFullPageDoc || onboardingLocked) {
      if (activeReportId) selectReport(null);
      if (open) closePanel();
      return;
    }
    if (view === 'website-preview' && !githubPreviewEnabled) {
      if (open) closePanel();
      setView('document');
      return;
    }
    if (view === 'canvas') {
      if (isDesktop === false || canvasAvailable === false) {
        if (open) closePanel();
        setView('document');
      }
      return;
    }
    if (view === 'design-system') {
      if (isDesktop === false || !designSystemEnabled) {
        if (open) closePanel();
        setView('document');
      }
      return;
    }
    if (
      view === 'website-preview' ||
      isDesktop !== false ||
      authPreview ||
      !open ||
      !activeReportId
    ) {
      return;
    }
    // Consume the embedded selection so leaving the full-page Doc cannot redirect back to it.
    selectReport(null);
    closePanel();
    router.replace(`/app/docs/${encodeURIComponent(activeReportId)}`);
  }, [
    activeCanvas?.id,
    activeReportId,
    authPreview,
    closePanel,
    clearCanvas,
    canvasAvailable,
    designSystemEnabled,
    githubPreviewEnabled,
    isDesktop,
    isFullPageDoc,
    onboardingLocked,
    open,
    router,
    selectReport,
    setView,
    view,
  ]);
  if (authPreview || onboardingLocked || !currentProject?.id || isFullPageDoc) {
    return null;
  }
  if (view === 'tools') {
    if (!open || !isDesktop) return null;
    return (
      <SidePanelTools
        onClose={closePanel}
        onOpenDocs={() => {
          selectReport(null);
          setView('document');
          openPanel();
        }}
        onOpenComments={() => setView('comments')}
        onOpenWebsitePreview={githubPreviewAvailable ? handleWebsitePreviewOpen : undefined}
        onOpenCanvas={canvasAvailable === true ? () => handleCanvasOpen() : undefined}
        onOpenDesignSystem={designSystemEnabled ? handleDesignSystemOpen : undefined}
      />
    );
  }
  if (view === 'website-preview') {
    if (!githubPreviewEnabled) return null;
    return (
      <WorkspaceWebsitePreviewPanel
        preview={activeWebsitePreview}
        open={open}
        onLaunchPreview={launchWebsitePreview}
        onOpenChange={handleWebsitePreviewPanelOpenChange}
      />
    );
  }
  if (view === 'canvas' && canvasAvailable === true && isDesktop) {
    return (
      <WorkspaceCanvasPanel
        projectId={currentProject.id}
        activeCanvas={activeCanvas}
        open={open}
        onOpenChange={handleCanvasPanelOpenChange}
        onBrowseLibrary={() => {
          handleCanvasPanelOpenChange(false);
          selectReport(null);
          openPanel();
        }}
        onSelectCanvas={handleCanvasSelect}
        onCanvasCreated={ensureWorkspaceChat}
        onClearCanvas={clearCanvas}
        onSelectionChange={setCanvasSelection}
        onSelectionClearHandlerChange={registerCanvasSelectionClear}
      />
    );
  }
  if (view === 'design-system' && designSystemEnabled && isDesktop) {
    return (
      <WorkspaceDesignSystemPanel
        projectId={currentProject.id}
        open={open}
        onOpenChange={handleDesignSystemPanelOpenChange}
      />
    );
  }
  if (!isDesktop) return null;
  return (
    <WorkspaceLivingDocsPanel
      projectId={currentProject.id}
      activeReportId={activeReportId}
      open={open}
      commentsOpen={view === 'comments'}
      onOpenChange={nextOpen => (nextOpen ? openPanel() : closePanel())}
      onCommentsOpenChange={nextOpen => setView(nextOpen ? 'comments' : 'document')}
      onSelectReport={selectReport}
      canvasAvailable={canvasAvailable === true}
      onSelectCanvas={handleCanvasOpen}
    />
  );
}

function AppLayoutContent({
  children,
  initialIsAdmin = false,
  authPreview = false,
}: AppLayoutProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isOnboardingWorkspace =
    pathname === '/app/workspace' &&
    (searchParams.get('new_project') === 'true' ||
      searchParams.get('setup') === 'true' ||
      (searchParams.get('onboarding_chat') === 'true' &&
        !searchParams.get('session') &&
        !searchParams.get('sessionId')));

  return (
    <LivingDocsPanelProvider>
      <VoiceBoundary>
        <MobileThreadsProvider>
          {/* Main App Layout */}
          <Flex
            flex="1"
            p="0"
            minW={0}
            minH="0"
            h="full"
            overflow="hidden"
            position="relative"
            bg="app.bg"
          >
            <PrimaryRail
              initialIsAdmin={initialIsAdmin}
              authPreview={authPreview}
              onboardingLocked={isOnboardingWorkspace}
            />
            <Flex
              data-section="appShellFrame"
              flex="1"
              minW={0}
              minH={0}
              position="relative"
              overflow="hidden"
              my={{ base: 0, lg: 1 }}
              mr={{ base: 0, lg: 3 }}
              borderRadius={{ base: 0, lg: '20px' }}
              borderWidth={{ base: 0, lg: '1px' }}
              borderColor="ui.border.subtle"
              bg="ui.surface.panel"
            >
              {getPrimarySection(pathname, searchParams) !== 'library' ? (
                <Box position="relative" h="full" flexShrink={0} zIndex={3}>
                  <AppRail authPreview={authPreview} initialIsAdmin={initialIsAdmin} />
                </Box>
              ) : null}
              <Stack
                flex="1"
                alignItems="stretch"
                w="full"
                minW={0}
                minH={0}
                h="full"
                gap="0"
                overflow="hidden"
                position="relative"
                zIndex={2}
              >
                <Flex flex="1" flexBasis="0" minH={0} minW={0} overflow="hidden" position="relative">
                  <Box
                    w="full"
                    maxW="none"
                    flex="1"
                    flexBasis="0"
                    display="flex"
                    flexDirection="column"
                    minH={0}
                    overflow="hidden"
                    minW={0}
                    position="relative"
                    zIndex={0}
                    isolation="isolate"
                    containerType="inline-size"
                    containerName="app-content"
                  >
                    <Box w="full" maxW="none" position="sticky" flexShrink={0} zIndex="docked">
                      <AppSidebar initialIsAdmin={initialIsAdmin} authPreview={authPreview} />
                    </Box>
                    <AppShellToolbarFrame>
                      <Box
                        w="full"
                        flex="1"
                        flexBasis="0"
                        display="flex"
                        flexDirection="column"
                        alignItems={authPreview ? 'center' : 'stretch'}
                        justifyContent={authPreview ? 'center' : 'flex-start'}
                        minH={0}
                        overflowX="hidden"
                        overflowY="auto"
                        minW={0}
                        px={authPreview ? { base: 4, md: 8 } : undefined}
                        py={authPreview ? { base: 8, md: 10 } : undefined}
                        pb={
                          authPreview
                            ? { base: '84px', md: 10 }
                            : isOnboardingWorkspace
                              ? 0
                              : { base: '76px', lg: 0 }
                        }
                        scrollPaddingBottom={
                          authPreview
                            ? { base: '84px', md: 10 }
                            : isOnboardingWorkspace
                              ? 0
                              : { base: '84px', lg: 0 }
                        }
                      >
                        {children}
                      </Box>
                    </AppShellToolbarFrame>
                  </Box>
                  <GlobalLivingDocsPanel
                    authPreview={authPreview}
                    onboardingLocked={isOnboardingWorkspace}
                  />
                </Flex>
              </Stack>
            </Flex>
          </Flex>
        </MobileThreadsProvider>
      </VoiceBoundary>
    </LivingDocsPanelProvider>
  );
}

export function AppLayout(props: AppLayoutProps) {
  return (
    <AppRailProvider>
      <DownloadsProvider>
        <SidePanelSizeProvider>
          <AppShellHeaderProvider>
            <AppLayoutContent {...props} />
          </AppShellHeaderProvider>
        </SidePanelSizeProvider>
      </DownloadsProvider>
    </AppRailProvider>
  );
}
